import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { resolveImg } from "@/lib/api";
import { useContent } from "@/lib/content";

export default function Hero({ heroUrl, onScrollDown }) {
  const c = useContent();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const fgOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100svh] overflow-hidden flex items-center justify-center"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        {heroUrl ? (
          <img
            src={resolveImg(heroUrl)}
            alt="Full Stack Gym"
            data-testid="hero-background-image"
            className="h-full w-full object-cover opacity-45"
          />
        ) : (
          <div className="h-full w-full bg-[#0c0a08]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/30 to-ink" />
        <div className="absolute inset-0 [box-shadow:inset_0_0_220px_80px_rgba(5,5,5,0.9)]" />
      </motion.div>

      <motion.div style={{ opacity: fgOpacity }} className="relative z-10 flex flex-col items-center px-6 w-full">
        <h1 className="sr-only">Full Stack 24/7 Gym — Palestra e Personal Trainer a Gioia Tauro</h1>
        <motion.img
          src="/assets/logo-full.png"
          alt="Full Stack 24/7 Gym"
          data-testid="hero-logo"
          initial={{ clipPath: "inset(0 100% 0 0)", opacity: 0, scale: 0.96 }}
          animate={{ clipPath: "inset(0 0% 0 0)", opacity: 1, scale: 1 }}
          transition={{ delay: 0.45, duration: 1.3, ease: [0.16, 1, 0.3, 1] }}
          className="w-[92vw] max-w-3xl md:max-w-5xl object-contain drop-shadow-[0_10px_40px_rgba(0,0,0,0.8)]"
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.9 }}
          className="mt-8 flex items-center gap-4 md:gap-6"
        >
          <span className="h-px w-10 md:w-20 bg-gold/60" />
          <p className="font-display text-bone/90 text-lg md:text-2xl tracking-[0.35em] text-center">
            {c.hero_tagline}
          </p>
          <span className="h-px w-10 md:w-20 bg-gold/60" />
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-down"
        onClick={onScrollDown}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-bone/50 hover:text-gold transition-colors duration-300"
        aria-label="Scorri giù"
      >
        <span className="font-display tracking-[0.3em] text-sm">SCORRI</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ChevronDown size={20} />
        </motion.span>
      </motion.button>
    </section>
  );
}
